if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
          overlay: this.querySelector('.country-selector__overlay'),
        };
        this.mobilePortalPlaceholder = null;
        this.mobilePortalOpen = false;
        this.mobilePortalMenuDrawer = null;
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.onFocusOut = this.onFocusOut.bind(this);
        this.addEventListener('focusout', this.onFocusOut);
        this.elements.button.addEventListener('click', this.openSelector.bind(this));
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
        document.addEventListener('pointerdown', this.onDocumentPointerDown);

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.hidePanel();
            if (this.elements.button) this.elements.button.focus({ preventScroll: true });
          });
        }
        if (this.elements.overlay) {
          this.elements.overlay.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.hidePanel();
          });
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      onDocumentPointerDown(event) {
        if (this.elements.button.getAttribute('aria-expanded') !== 'true') return;

        // On mobile the country sheet is portaled to <body> so it can sit above
        // the header/menu stacking contexts. Treat the portaled sheet as part of
        // this component for outside-click purposes.
        if (!this.mql.matches && this.mobilePortalOpen) {
          if (
            event.target === this.elements.button ||
            this.elements.button.contains(event.target) ||
            this.elements.panel.contains(event.target) ||
            this.elements.overlay.contains(event.target)
          ) return;
          this.hidePanel();
          return;
        }

        if (this.contains(event.target)) return;
        this.hidePanel();
      }

      portalMobilePanel() {
        if (this.mql.matches || this.mobilePortalOpen || !this.elements.panel || !this.elements.overlay) return;
        const parent = this.elements.panel.parentNode;
        if (!parent) return;

        this.mobilePortalPlaceholder = document.createComment('oc-mobile-country-portal');
        parent.insertBefore(this.mobilePortalPlaceholder, this.elements.panel);
        this.mobilePortalMenuDrawer = this.closest('.menu-drawer');

        this.elements.panel.classList.add('oc-mobile-country-portal');
        this.elements.overlay.classList.add('oc-mobile-country-portal-overlay');
        document.body.appendChild(this.elements.overlay);
        document.body.appendChild(this.elements.panel);
        this.mobilePortalOpen = true;
      }

      restoreMobilePanel() {
        if (!this.mobilePortalOpen) return;
        const marker = this.mobilePortalPlaceholder;
        if (marker && marker.parentNode) {
          marker.parentNode.insertBefore(this.elements.panel, marker.nextSibling);
          this.elements.panel.after(this.elements.overlay);
          marker.remove();
        }
        this.elements.panel.classList.remove('oc-mobile-country-portal');
        this.elements.overlay.classList.remove('oc-mobile-country-portal-overlay');
        this.mobilePortalPlaceholder = null;
        this.mobilePortalOpen = false;
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        this.elements.panel.classList.remove('is-submitting');
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('oc-country-sheet-open');
        if (this._addedMobileOverflowLock) {
          document.body.classList.remove('overflow-hidden-mobile');
          this._addedMobileOverflowLock = false;
        }
        const menuDrawer = this.mobilePortalMenuDrawer || this.closest('.menu-drawer');
        if (menuDrawer) menuDrawer.classList.remove('country-selector-open');
        this.restoreMobilePanel();
        this.mobilePortalMenuDrawer = null;
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.elements.panel.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        event.stopPropagation();
        const form = this.querySelector('form');
        const value = event.currentTarget.dataset.value;
        if (!form || !this.elements.input || !value) return;

        this.elements.input.value = value;
        this.elements.input.dispatchEvent(new Event('change', { bubbles: true }));

        // Desktop and mobile intentionally use the exact same Shopify
        // localization submission path. Mobile differs only in presentation:
        // its country panel is portaled above the UI.
        const returnTo = form.querySelector('input[name="return_to"]');
        if (returnTo) returnTo.value = `${window.location.pathname}${window.location.search}`;
        this.elements.panel.classList.add('is-submitting');
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else HTMLFormElement.prototype.submit.call(form);
        this.hidePanel();
      }

      openSelector(event) {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        const isOpening = this.elements.button.getAttribute('aria-expanded') !== 'true';

        if (!isOpening) {
          this.hidePanel();
          return;
        }

        if (!this.mql.matches) this.portalMobilePanel();
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');

        if (!document.body.classList.contains('overflow-hidden-tablet') && !document.body.classList.contains('overflow-hidden-mobile')) {
          document.body.classList.add('overflow-hidden-mobile');
          this._addedMobileOverflowLock = true;
        }
        if (!this.mql.matches) document.body.classList.add('oc-country-sheet-open');

        // Desktop keeps Dawn's keyboard-first behavior. Mobile intentionally does
        // not force focus, so tapping a country cannot be pre-empted by blur.
        if (this.mql.matches) {
          this.elements.button.focus({ preventScroll: true });
          if (this.elements.search) this.elements.search.focus({ preventScroll: true });
        }
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        const menuDrawer = this.mobilePortalMenuDrawer || this.closest('.menu-drawer');
        if (menuDrawer) menuDrawer.classList.add('country-selector-open');
      }

      onFocusOut(event) {
        // Touch browsers frequently report relatedTarget=null while tapping a
        // country option. Let the explicit X / overlay / outside-tap handlers
        // own closing on mobile; keep Dawn's focusout behavior on desktop.
        if (!this.mql.matches) return;
        this.closeSelector(event);
      }

      closeSelector(event) {
        const targetInside = this.contains(event.target) || this.elements.panel.contains(event.target);
        const relatedInside = event.relatedTarget && (this.contains(event.relatedTarget) || this.elements.panel.contains(event.relatedTarget));
        if (event.target.classList.contains('country-selector__overlay') || !targetInside || !relatedInside) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.elements.panel.querySelector('.popular-countries');
        const allCountries = this.elements.panel.querySelectorAll('a.disclosure__link');
        let visibleCountries = allCountries.length;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.elements.panel.scrollTop = 0;
        const countryList = this.elements.panel.querySelector('.country-selector__list');
        if (countryList) countryList.scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
